import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { supabase } from '@/client/lib/supabase';
import { useAuth } from '@/client/hooks/useAuth';

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const router = useRouter();
  const { updatePassword, loading, error, setError } = useAuth();
  const [sessionReady, setSessionReady] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [matchError, setMatchError] = useState<string | null>(null);

  // Listen for the PASSWORD_RECOVERY auth event fired when Supabase
  // processes the recovery token from the URL hash.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
      }
    });

    // Fallback: session may already be established if Supabase processed
    // the URL before this effect ran.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setSessionReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Show an error if no recovery session arrives within 5 seconds.
  useEffect(() => {
    if (sessionReady) return;
    const timer = setTimeout(() => setInvalidLink(true), 5000);
    return () => clearTimeout(timer);
  }, [sessionReady]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMatchError(null);
    setError(null);
    if (newPassword !== confirmPassword) {
      setMatchError('Passwords do not match.');
      return;
    }
    const ok = await updatePassword(newPassword);
    if (ok) router.navigate({ to: '/' });
  }

  if (invalidLink && !sessionReady) {
    return (
      <div className="flex h-screen items-center justify-center p-10">
        <div className="flex w-full max-w-[360px] flex-col gap-4">
          <h1 className="m-0 text-[22px] font-semibold tracking-tight text-slate-900">
            Link expired
          </h1>
          <p className="text-[14px] leading-relaxed text-neutral-500">
            This reset link is invalid or has expired. Please request a new one.
          </p>
          <a
            href="/login"
            className="self-start text-[13px] text-blue-600 underline-offset-2 hover:underline"
          >
            ← Back to sign in
          </a>
        </div>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-[14px] text-neutral-500">Verifying link…</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center p-10">
      <div className="flex w-full max-w-[360px] flex-col gap-[22px]">
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 text-[26px] font-semibold tracking-tight text-slate-900">
            Set new password
          </h1>
          <p className="m-0 text-[13px] text-neutral-500">
            Choose a strong password for your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="new-password" className="text-[13px] font-medium text-slate-800">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
              className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="confirm-password" className="text-[13px] font-medium text-slate-800">
              Confirm password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
              className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          {(matchError || error) && (
            <p className="text-[13px] text-red-600" role="alert">
              {matchError ?? error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-0.5 flex h-[42px] w-full cursor-pointer items-center justify-center rounded-lg bg-slate-900 text-[15px] font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}
